import { pb, User, isUser } from './pocketbase';
import { ClientResponseError } from 'pocketbase';
import { API_URL } from './config';

export interface AuthSession {
  token: string;
  user: User;
}

export interface SignupData {
  email: string;
  password: string;
  name: string;
  marketingConsent?: boolean;
  promoCode?: string;
}

export interface AuthError {
  message: string;
  code?: string;
  field?: string;
}

export interface TelegramAuthData {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

/**
 * Парсинг ошибок Pocketbase в читаемый формат
 */
function parseAuthError(error: unknown): AuthError {
  if (error instanceof ClientResponseError) {
    const data = error.data;

    // Проверяем ошибки валидации полей
    if (data?.data) {
      const fieldErrors = data.data;
      if (fieldErrors.email?.code === 'validation_invalid_email') {
        return { message: 'Некорректный email адрес', field: 'email' };
      }
      if (fieldErrors.email?.code === 'validation_not_unique') {
        return { message: 'Пользователь с таким email уже существует', field: 'email' };
      }
      if (fieldErrors.password?.code) {
        return { message: 'Пароль должен содержать минимум 8 символов', field: 'password' };
      }
    }

    // Общие ошибки аутентификации
    if (error.status === 400) {
      return { message: 'Неверный email или пароль' };
    }
    if (error.status === 401) {
      return { message: 'Ошибка авторизации' };
    }
    if (error.status === 403) {
      return { message: 'Доступ запрещён' };
    }

    return { message: error.message || 'Произошла ошибка' };
  }

  return { message: 'Произошла неизвестная ошибка' };
}

/**
 * Регистрация нового пользователя
 */
export async function signup(data: SignupData): Promise<AuthSession> {
  try {
    // Создаём пользователя
    await pb.collection('users').create({
      email: data.email,
      password: data.password,
      passwordConfirm: data.password,
      name: data.name,
      subscription_tier: 'free',
      marketing_consent: data.marketingConsent || false,
      promo_code_used: data.promoCode || '',
    });

    // Сразу логинимся
    const authData = await pb.collection('users').authWithPassword(data.email, data.password);

    const record = authData.record;
    if (!isUser(record)) {
      throw new Error('Invalid user record from server');
    }
    return {
      token: authData.token,
      user: record,
    };
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Вход по email и паролю
 */
export async function login(email: string, password: string): Promise<AuthSession> {
  try {
    const authData = await pb.collection('users').authWithPassword(email, password);

    const record = authData.record;
    if (!isUser(record)) {
      throw new Error('Invalid user record from server');
    }
    return {
      token: authData.token,
      user: record,
    };
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Проверка и обновление текущей сессии
 */
export async function checkSession(): Promise<AuthSession | null> {
  if (!pb.authStore.isValid) {
    return null;
  }

  try {
    const authData = await pb.collection('users').authRefresh();
    const record = authData.record;
    if (!isUser(record)) {
      return null;
    }
    return {
      token: authData.token,
      user: record,
    };
  } catch {
    // authRefresh fails for impersonation tokens (Telegram auth) because PocketBase
    // only accepts standard "authRecord" tokens on the auth-refresh endpoint.
    // Validate the session by fetching the user record directly instead.
    const storedId = (pb.authStore.record?.id) as string | undefined;
    if (storedId) {
      try {
        const record = await pb.collection('users').getOne(storedId);
        if (isUser(record)) {
          return { token: pb.authStore.token, user: record };
        }
      } catch {
        // Token is genuinely invalid
      }
    }
    pb.authStore.clear();
    return null;
  }
}

/**
 * Запрос сброса пароля
 */
export async function requestPasswordReset(email: string): Promise<void> {
  try {
    await pb.collection('users').requestPasswordReset(email);
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Подтверждение сброса пароля
 */
export async function confirmPasswordReset(
  token: string,
  password: string
): Promise<void> {
  try {
    await pb.collection('users').confirmPasswordReset(token, password, password);
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Запрос подтверждения email
 */
export async function requestEmailVerification(email: string): Promise<void> {
  try {
    await pb.collection('users').requestVerification(email);
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Подтверждение email
 */
export async function confirmEmailVerification(token: string): Promise<void> {
  try {
    await pb.collection('users').confirmVerification(token);
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Обновление профиля пользователя
 */
export async function updateProfile(
  userId: string,
  data: Partial<Pick<User, 'name' | 'avatar' | 'marketing_consent'>>
): Promise<User> {
  try {
    const updated = await pb.collection('users').update(userId, data);
    if (!isUser(updated)) {
      throw new Error('Invalid user record from server');
    }
    return updated;
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Смена пароля (требует текущий пароль)
 */
export async function changePassword(
  userId: string,
  currentPassword: string,
  newPassword: string
): Promise<void> {
  try {
    await pb.collection('users').update(userId, {
      oldPassword: currentPassword,
      password: newPassword,
      passwordConfirm: newPassword,
    });
  } catch (error) {
    throw parseAuthError(error);
  }
}

/**
 * Удаление аккаунта пользователя (через бэкенд — superuser удаляет все данные)
 */
export async function deleteAccount(_userId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/account`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Не удалось удалить аккаунт');
  }

  pb.authStore.clear();
}

/**
 * Bot-based Telegram auth: initialize (get code + bot URL)
 */
export async function initTelegramBotAuth(): Promise<{ code: string; botUrl: string }> {
  const response = await fetch(`${API_URL}/api/auth/telegram-bot/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as Record<string, string>;
    throw { message: errorData.error || 'Ошибка инициализации Telegram авторизации' } as AuthError;
  }

  return response.json() as Promise<{ code: string; botUrl: string }>;
}

/**
 * Bot-based Telegram auth: check if completed
 */
export async function checkTelegramBotAuth(code: string): Promise<{
  status: 'pending' | 'completed';
  token?: string;
  record?: Record<string, unknown>;
}> {
  const response = await fetch(`${API_URL}/api/auth/telegram-bot/check?code=${encodeURIComponent(code)}`);

  if (!response.ok) {
    throw { message: 'Ошибка проверки статуса авторизации' } as AuthError;
  }

  return response.json() as Promise<{
    status: 'pending' | 'completed';
    token?: string;
    record?: Record<string, unknown>;
  }>;
}

/**
 * Привязка Telegram к текущему аккаунту
 */
export async function linkTelegram(data: TelegramAuthData): Promise<void> {
  const response = await fetch(`${API_URL}/api/auth/telegram/link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as Record<string, string>;
    throw { message: errorData.error || 'Не удалось привязать Telegram' } as AuthError;
  }
}

/**
 * Отвязка Telegram от текущего аккаунта
 */
export async function unlinkTelegram(): Promise<void> {
  const response = await fetch(`${API_URL}/api/auth/telegram/unlink`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as Record<string, string>;
    throw { message: errorData.error || 'Не удалось отвязать Telegram' } as AuthError;
  }
}
